import React from "react";
import {
  ArrowDown,
  ArrowUp,
  Camera,
  Eye,
  MousePointerClick,
  PenLine,
  Plus,
  Timer,
  Trash2,
  Wand2,
  Workflow as WfIcon,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { useSystemConfig } from "@/lib/systemConfig";

// Sec-S4 (2026-05-26): `evaluate_js` was previously NOT exposed in the
// editor at all. Added here behind the `evaluate_js_enabled` flag so
// admins on `ALLOW_DISCOVERY=true` backends can author discovery
// steps interactively. Anyone else (any non-admin, OR an admin on a
// production backend with ALLOW_DISCOVERY=false) won't even see the
// option — same defence-in-depth as the backend gate.
const BASE_ACTIONS = [
  { value: "goto", label: "Go to URL", icon: WfIcon },
  { value: "fill", label: "Fill field", icon: PenLine },
  { value: "click", label: "Click", icon: MousePointerClick },
  { value: "wait", label: "Wait", icon: Timer },
  { value: "screenshot", label: "Screenshot", icon: Camera },
  { value: "assertion", label: "Assertion", icon: Eye },
  { value: "settle_delay", label: "Settle delay (Phase 2)", icon: Wand2 },
];

const PRIVILEGED_ACTIONS = [
  { value: "evaluate_js", label: "Evaluate JS (admin · discovery)", icon: Eye },
];

const SELECTOR_STRATEGIES = [
  { value: "css", label: "CSS selector" },
  { value: "getByLabel", label: "By accessible label" },
  { value: "getByRole", label: "By ARIA role" },
  { value: "getByTestId", label: "By data-testid" },
  { value: "getByText", label: "By text content" },
];

const ASSERTION_TYPES = [
  { value: "url_contains", label: "URL contains" },
  { value: "text_visible", label: "Text is visible" },
  { value: "element_visible", label: "Element is visible" },
];

function emptyStep(order) {
  return {
    id: Math.random().toString(36).slice(2),
    order,
    name: "",
    action: "goto",
    url: "",
    selector: "",
    selector_strategy: "css",
    value: "",
    wait_ms: 1000,
    screenshot_name: "",
    assertion: null,
    timeout_ms: 15000,
    continue_on_failure: false,
  };
}

export function StepBuilder({ value = [], onChange }) {
  // Sec-S4: compute the action menu once per render based on the
  // backend-supplied evaluate_js gate.  When `false`, the privileged
  // action is *not even rendered* in the select — defence-in-depth
  // alongside the runtime gate in routes/demo_runs.py.
  const { config } = useSystemConfig();
  const actions = React.useMemo(
    () => (config.evaluate_js_enabled ? [...BASE_ACTIONS, ...PRIVILEGED_ACTIONS] : BASE_ACTIONS),
    [config.evaluate_js_enabled],
  );

  function update(i, patch) {
    const next = value.map((s, idx) => (idx === i ? { ...s, ...patch } : s));
    onChange?.(next);
  }
  function add() {
    onChange?.([...value, emptyStep(value.length + 1)]);
  }
  function remove(i) {
    onChange?.(value.filter((_, idx) => idx !== i).map((s, idx) => ({ ...s, order: idx + 1 })));
  }
  function move(i, dir) {
    const j = i + dir;
    if (j < 0 || j >= value.length) return;
    const next = value.slice();
    [next[i], next[j]] = [next[j], next[i]];
    onChange?.(next.map((s, idx) => ({ ...s, order: idx + 1 })));
  }

  return (
    <div className="space-y-3" data-testid="step-builder">
      {value.length === 0 && (
        <p
          className="rounded-lg border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-400"
          data-testid="steps-empty"
        >
          No steps yet. Add one to get started — you can save an empty draft.
        </p>
      )}
      {value.map((s, i) => (
        <StepCard
          key={s.id || i}
          step={s}
          index={i}
          actions={actions}
          isFirst={i === 0}
          isLast={i === value.length - 1}
          onChange={(patch) => update(i, patch)}
          onRemove={() => remove(i)}
          onMoveUp={() => move(i, -1)}
          onMoveDown={() => move(i, 1)}
        />
      ))}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={add}
        data-testid="step-add-button"
      >
        <Plus size={14} className="mr-1.5" />
        Add step
      </Button>
    </div>
  );
}

function StepCard({ step, index, actions, isFirst, isLast, onChange, onRemove, onMoveUp, onMoveDown }) {
  const ActionIcon = actions.find((a) => a.value === step.action)?.icon || WfIcon;
  const showSelector = ["fill", "click", "wait"].includes(step.action);
  const showUrl = step.action === "goto";
  const showValue = step.action === "fill" || step.action === "evaluate_js";
  const showWaitMs = ["wait", "settle_delay"].includes(step.action);
  const showScreenshot = step.action === "screenshot";
  const showAssertion = step.action === "assertion";

  return (
    <div
      data-testid={`step-card-${index}`}
      className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
    >
      <div className="flex items-start gap-3">
        <div className="grid h-8 w-8 flex-none place-items-center rounded-md bg-indigo-50 text-indigo-700">
          <ActionIcon size={16} />
        </div>
        <div className="grid w-12 flex-none">
          <span className="text-[11px] uppercase tracking-wider text-slate-400">Step</span>
          <span className="font-mono text-lg font-semibold text-slate-700">
            {step.order || index + 1}
          </span>
        </div>
        <div className="flex-1 space-y-3">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-12">
            <div className="md:col-span-6">
              <Label className="text-xs text-slate-500">Name</Label>
              <Input
                value={step.name}
                onChange={(e) => onChange({ name: e.target.value })}
                placeholder="Click Sign In"
                data-testid={`step-name-${index}`}
              />
            </div>
            <div className="md:col-span-4">
              <Label className="text-xs text-slate-500">Action</Label>
              <Select
                value={step.action}
                onValueChange={(v) => onChange({ action: v })}
              >
                <SelectTrigger data-testid={`step-action-${index}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ACTIONS.map((a) => (
                    <SelectItem key={a.value} value={a.value} data-testid={`step-action-${index}-${a.value}`}>
                      {a.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs text-slate-500">Timeout (ms)</Label>
              <Input
                type="number"
                value={step.timeout_ms ?? 15000}
                onChange={(e) =>
                  onChange({ timeout_ms: Number(e.target.value) || 0 })
                }
                data-testid={`step-timeout-${index}`}
              />
            </div>
          </div>

          {showUrl && (
            <div>
              <Label className="text-xs text-slate-500">URL (supports ${"{VAR}"} interpolation)</Label>
              <Input
                value={step.url || ""}
                onChange={(e) => onChange({ url: e.target.value })}
                placeholder="https://ibuy.cloud or ${EYVO_DEMO_URL}"
                data-testid={`step-url-${index}`}
              />
            </div>
          )}

          {showSelector && (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-12">
              <div className="md:col-span-8">
                <Label className="text-xs text-slate-500">Selector</Label>
                <Input
                  value={step.selector || ""}
                  onChange={(e) => onChange({ selector: e.target.value })}
                  placeholder={
                    step.selector_strategy === "css"
                      ? "[data-testid=login-submit-button]"
                      : step.selector_strategy === "getByRole"
                      ? "Sign In"
                      : "login-submit-button"
                  }
                  data-testid={`step-selector-${index}`}
                  className="font-mono text-sm"
                />
              </div>
              <div className="md:col-span-4">
                <Label className="text-xs text-slate-500">Strategy</Label>
                <Select
                  value={step.selector_strategy || "css"}
                  onValueChange={(v) => onChange({ selector_strategy: v })}
                >
                  <SelectTrigger data-testid={`step-strategy-${index}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SELECTOR_STRATEGIES.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {showValue && (
            <div>
              <Label className="text-xs text-slate-500">Value to fill (supports ${"{VAR}"})</Label>
              <Input
                value={step.value || ""}
                onChange={(e) => onChange({ value: e.target.value })}
                placeholder="${supplier_name}"
                data-testid={`step-value-${index}`}
                className="font-mono text-sm"
              />
            </div>
          )}

          {showWaitMs && (
            <div className="md:w-1/3">
              <Label className="text-xs text-slate-500">
                {step.action === "settle_delay" ? "Settle delay (ms) — Phase 2" : "Wait (ms)"}
              </Label>
              <Input
                type="number"
                value={step.wait_ms ?? 0}
                onChange={(e) => onChange({ wait_ms: Number(e.target.value) || 0 })}
                data-testid={`step-wait-${index}`}
              />
            </div>
          )}

          {showScreenshot && (
            <div>
              <Label className="text-xs text-slate-500">Screenshot file name</Label>
              <Input
                value={step.screenshot_name || ""}
                onChange={(e) => onChange({ screenshot_name: e.target.value })}
                placeholder="dashboard_loaded"
                data-testid={`step-screenshot-name-${index}`}
              />
            </div>
          )}

          {showAssertion && (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-12">
              <div className="md:col-span-4">
                <Label className="text-xs text-slate-500">Assertion type</Label>
                <Select
                  value={step.assertion?.type || "url_contains"}
                  onValueChange={(v) =>
                    onChange({
                      assertion: { type: v, expected: step.assertion?.expected || "" },
                    })
                  }
                >
                  <SelectTrigger data-testid={`step-assertion-type-${index}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ASSERTION_TYPES.map((a) => (
                      <SelectItem key={a.value} value={a.value}>
                        {a.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-8">
                <Label className="text-xs text-slate-500">Expected</Label>
                <Input
                  value={step.assertion?.expected || ""}
                  onChange={(e) =>
                    onChange({
                      assertion: {
                        type: step.assertion?.type || "url_contains",
                        expected: e.target.value,
                      },
                    })
                  }
                  placeholder="/dashboard"
                  data-testid={`step-assertion-expected-${index}`}
                />
              </div>
            </div>
          )}

          <div className="flex items-center gap-2 pt-1">
            <Checkbox
              id={`step-continue-${index}`}
              checked={!!step.continue_on_failure}
              onCheckedChange={(c) => onChange({ continue_on_failure: !!c })}
              data-testid={`step-continue-${index}`}
            />
            <Label htmlFor={`step-continue-${index}`} className="text-sm text-slate-600">
              Continue on failure
            </Label>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <Button
            type="button"
            size="icon"
            variant="ghost"
            disabled={isFirst}
            onClick={onMoveUp}
            data-testid={`step-move-up-${index}`}
          >
            <ArrowUp size={16} />
          </Button>
          <Button
            type="button"
            size="icon"
            variant="ghost"
            disabled={isLast}
            onClick={onMoveDown}
            data-testid={`step-move-down-${index}`}
          >
            <ArrowDown size={16} />
          </Button>
          <Button
            type="button"
            size="icon"
            variant="ghost"
            onClick={onRemove}
            className="text-red-600 hover:bg-red-50 hover:text-red-700"
            data-testid={`step-remove-${index}`}
          >
            <Trash2 size={16} />
          </Button>
        </div>
      </div>
    </div>
  );
}
